import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { cn } from "../../lib/cn";
import { Spinner } from "./Spinner";

export type ButtonVariant = "primary" | "secondary" | "danger" | "danger-outline" | "success" | "ghost" | "pay";
export type ButtonSize = "sm" | "md" | "xl";

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Disables the button and shows a spinner plus `loadingLabel` ("Saving…", §2.2). */
  loading?: boolean;
  loadingLabel?: ReactNode;
  icon?: ReactNode;
  block?: boolean;
  /** Icon-only button: requires an accessible label (§16.4). */
  iconOnly?: boolean;
  "aria-label"?: string;
};

export const Button = forwardRef<HTMLButtonElement, Props>(function Button(
  { variant = "primary", size = "md", loading, loadingLabel, icon, block, iconOnly, className, children, disabled, type = "button", ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn("btn", `btn-${variant}`, size === "sm" && "btn-sm", size === "xl" && "btn-xl", iconOnly && "btn-icon", block && "btn-block", className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <Spinner /> : icon}
      {loading && loadingLabel !== undefined ? loadingLabel : children}
    </button>
  );
});
